import {
  breakpoint,
  Pagination,
  InfoPanel,
  DropDown,
  DropDownOption,
  BodyText,
  LinkTextStyles,
} from '@grays-npm/component-library';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router-dom';
import {
  Loader,
  PageLayout,
  SortListings,
  useAuthData,
} from '../../components';
import { getListings } from '../../services/listings';
import { useCMSData } from '../../state/content-manager';
import { TabItem } from './Listings';
import { ListingsSection } from './ListingsSection';
import { NamedHeading, StyledHeading } from '../styles';
import { anchorClickHandler } from '../../utils/helpers';
import { QUERY_KEYS } from 'utils/constants';

const itemsPerPageOptions = [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
] as DropDownOption[];

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: ${({ theme }) => theme.spacings[5].rem} 0;

  ${breakpoint('lg')`
    margin: 0 0 ${({ theme }) => theme.spacings[7].rem};
  `}
`;

const StyledBodyText = styled(BodyText)`
  margin-bottom: ${({ theme }) => theme.spacings[5].rem};

  a {
    ${LinkTextStyles}
  }
`;

const DropdownsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  & > * {
    margin-left: ${({ theme }) => theme.spacings[4].rem};
  }
`;

const InfoPanelContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacings[4].rem};
`;

type ListingTabViewProps = {
  tabItem: TabItem;
  topChildren?: string | React.ReactNode;
  refetchData?: boolean;
};

export const ListingTabView: React.FC<ListingTabViewProps> = ({
  tabItem,
  topChildren,
  refetchData,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    itemsPerPageOptions[0].value
  );
  const [sortCode, setSortCode] = useState('');
  const { token, firstName } = useAuthData();
  const cmsData = useCMSData();
  let navigate = useNavigate();

  const infoPanels = cmsData?.listings.infoPanels ?? [];
  const tabInfoPanels = infoPanels.filter(
    (infoPanel) => infoPanel.tab === tabItem.type
  );

  const listingQuery = useQuery(
    [QUERY_KEYS.LISTINGS, tabItem.type, itemsPerPage, currentPage, token, sortCode],
    () => {
      const queryParams = {
        pageNumber: currentPage.toString(),
        pageSize: itemsPerPage,
        sortBy: sortCode,
      };
      return getListings(token, tabItem.type, queryParams);
    }
  );

  useEffect(
    () => {
      if (refetchData) listingQuery.refetch();
    },
    // We intentially do not want listingQuery in the array,
    // listingQuery.refetch() changes listing query, this would cause a loop.
    // eslint-disable-next-line
    [refetchData]
  );

  const pageCount = listingQuery.data?.pagination?.pageCount || 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (option: string) => {
    setItemsPerPage(option);
    TagManager.dataLayer({
      dataLayer: {
        event: `${tabItem.type.toLowerCase()}Listings-itemsPerPageOnClick`,
      },
    });
  };

  const handleSortChange = (value: string) => {
    setSortCode(value);
  };

  const visiblePage = listingQuery.data?.pagination.pageNumber ?? currentPage;

  const emptyDescriptionOnClick = (e: any) => {
    const link = anchorClickHandler(e);
    link && navigate(link);
  };

  return (
    <PageLayout
      topChildren={topChildren}
      bottomChildren={
        <Pagination
          currentPage={visiblePage}
          onPageChange={handlePageChange}
          pageCount={pageCount}
        />
      }
    >
      <Loader query={listingQuery}>
        {listingQuery?.data?.data && listingQuery.data.data.length > 0 ? (
          <>
            <Header>
              <StyledHeading size={4}>{tabItem.title}</StyledHeading>
              <DropdownsWrapper>
                <SortListings tab={tabItem.type} onSelect={handleSortChange} />
                <DropDown
                  align="right"
                  label="Items"
                  onChange={handleItemsPerPageChange}
                  options={itemsPerPageOptions}
                  selectedValue={itemsPerPage}
                />
              </DropdownsWrapper>
            </Header>
            {tabInfoPanels.map(({ title, type, message, link, linkLabel }) => (
              <InfoPanelContainer key={title}>
                <InfoPanel
                  title={title}
                  type={type}
                  link={link}
                  linkLabel={linkLabel}
                  // onClose={} TODO - Define close and actionClicks
                  // onActionClick={}
                >
                  <span dangerouslySetInnerHTML={{ __html: message }} />
                </InfoPanel>
              </InfoPanelContainer>
            ))}
            <ListingsSection listings={listingQuery.data.data} />
          </>
        ) : (
          <>
            <NamedHeading size={3}>Hi {firstName}!</NamedHeading>
            <StyledBodyText context="secondary" isUnderlined={false} size="lg">
              <strong>{tabItem.emptyText}</strong>
            </StyledBodyText>
            {tabItem.emptyDescription && (
              <StyledBodyText
                context="secondary"
                isUnderlined={false}
                size="md"
              >
                <span
                  role="link"
                  tabIndex={0}
                  dangerouslySetInnerHTML={{ __html: tabItem.emptyDescription }}
                  onClick={emptyDescriptionOnClick}
                  onKeyDown={emptyDescriptionOnClick}
                />
              </StyledBodyText>
            )}
          </>
        )}
      </Loader>
    </PageLayout>
  );
};
